import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { feedbackServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { Loader } from "../../components/CustomLoader";
import CustomPagination from "../../components/CustomPagination";
import { debounce } from "lodash";
import { fDate } from "../../utils/formatTime";
import { capitalizeFirstLetter, TrimText } from "../../utils/helpers";
import ViewFeedback from "./ViewFeedback";

const SORT_OPTIONS = [
  { label: "High to Low", value: "highToLow" },
  { label: "Low to High", value: "lowToHigh" },
];

export default function FeedbackManagementList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [sortBy, setSortBy] = useState([]);

  const fetchFeedback = async (query = searchTerm) => {
    const body = {
      pageNumber: currentPage,
      pageSize: pageSize,
      searchTerm: query || "",
      sortBy: sortBy || "",
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await feedbackServices.getAll(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.ratings || res?.data?.data || []);
          setTotalPages(res?.data?.data?.totalItems || res?.data?.total || 0);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData([]);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchFeedback(query), 400),
    [currentPage, pageSize, sortBy]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchTerm(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    fetchFeedback(searchTerm);
  }, [currentPage, pageSize, refresh, sortBy]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px",
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(TrimText(row.users[0]?.firstName + " " + row.users[0]?.lastName || row.userName || "", 25)),
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.users[0]?.email || "",
    },
    {
      name: "Message",
      sortable: true,
      selector: (row) => {
        const msg = row.message || row.feedback || "";
        const words = msg.trim().split(/\s+/);
        return words.length > 3 ? words.slice(0, 3).join(" ") + " ..." : msg;
      },
    },
    
    // {
    //   name: "Rating",
    //   sortable: true,
    //   cell: (row) => {
    //     const rating = row.rating || row.starRating || 0;
    //     return rating > 0 ? `${rating} ⭐` : "N/A";
    //   },
    //   width: "120px",
    // },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
      width: "150px",
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <Tooltip title="View" placement="top">
          <IconButton
            color="primary"
            onClick={() => {
              setModalShow(true);
              setViewId(row);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="Feedback Management" />
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-end"
        alignItems="center"
        p={2}
        gap={2}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Input
          value={searchTerm}
          placeholder="Search Feedback"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchTerm(cleared);
                  debouncedSearch(cleared);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {(sortBy || searchTerm) && (
          <Button
            variant="outlined"
            onClick={() => {
              setSortBy("");
              setSearchTerm("");
              setCurrentPage(1);
            }}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "100px",
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(1);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          className="custom"
        />
      </CardContent>

      {modalShow && (
        <ViewFeedback
          open={modalShow}
          onClose={() => setModalShow(false)}
          data={viewId}
        />
      )}
    </Card>
  );
}

